import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { consultationAPI, appointmentAPI, asyncCaseAPI } from '../services/api';
import AsyncCaseView from '../components/AsyncCaseView';

const Card = ({ title, children, style }) => (
  <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', ...style }}>
    {title && <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: 'var(--navy)' }}>{title}</h3>}
    {children}
  </div>
);

const InfoRow = ({ label, value, highlight }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
    <span style={{ color: 'var(--muted)' }}>{label}</span>
    <strong style={{ color: highlight ? 'var(--accent)' : 'var(--navy)' }}>{value || '—'}</strong>
  </div>
);

const Badge = ({ children, color }) => (
  <span style={{
    padding: '4px 12px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    background: color === 'green' ? 'rgba(13,154,106,0.15)' : color === 'red' ? 'rgba(192,24,90,0.15)' : 'rgba(138,151,168,0.15)',
    color: color === 'green' ? '#0D9A6A' : color === 'red' ? '#C0185A' : '#8A97A8'
  }}>
    {children}
  </span>
);

export default function ConsultationDetailPage() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [asyncCase, setAsyncCase] = useState(null); // @admin-async-case-v1
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const fetchData = async () => {
    try {
      const res = await consultationAPI.getByAppointment(appointmentId);
      setData(res.data.data);
      // @admin-async-case-v1: sick leave and repeat prescriptions have no video consultation, so the
      // cards below would all be empty. Show the screen the doctor worked in instead. A failure here
      // must never break the page, so each lookup falls back to "not this kind".
      const [sl, rn] = await Promise.all([
        asyncCaseAPI.sickLeave(appointmentId).then((r) => r.data?.data || null).catch(() => null),
        asyncCaseAPI.renewal(appointmentId).then((r) => r.data?.data || null).catch(() => null),
      ]);
      setAsyncCase(sl ? { kind: 'sick_leave', data: sl } : rn ? { kind: 'renewal', data: rn } : null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load consultation');
    } finally {
      setLoading(false);
    }
  };

  // @m24-deps: dependency list left exactly as it was when linting was switched on 25 Sep 2026.
  // Adding the missing dependency can change how often this effect runs, so it is a behaviour
  // change to make deliberately and test, not a lint tidy-up. Recorded, not silenced by turning
  // the rule off - it still guards every effect written from here on.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [appointmentId]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await appointmentAPI.cancelByAdmin(appointmentId, cancelReason);
      alert('✅ Το ραντεβού ακυρώθηκε');
      setShowCancelModal(false);
      setCancelReason('');
      fetchData();
    } catch (err) {
      alert('Σφάλμα: ' + (err.response?.data?.error || 'Αποτυχία ακύρωσης'));
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Φόρτωση...</div>;
  if (error) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--accent)' }}>⚠️ {error}</div>;
  if (!data) return <div style={{ padding: 40, textAlign: 'center' }}>Δεν βρέθηκαν δεδομένα</div>;

  const isCancelled = data.status && data.status.startsWith('cancelled');
  const isCompleted = data.status === 'completed';
  const canCancel = !isCancelled && !isCompleted && data.status !== 'no_show';

  const statusColor = isCompleted ? 'green' : isCancelled ? 'red' : 'gray';
  const statusLabel = {
    completed: 'Ολοκληρώθηκε',
    cancelled: 'Ακυρώθηκε',
    cancelled_by_patient: 'Ακύρωση από ασθενή',
    cancelled_by_doctor: 'Ακύρωση από ιατρό',
    cancelled_by_admin: 'Ακύρωση από διαχειριστή',
    scheduled: 'Προγραμματισμένο',
    in_progress: 'Σε Εξέλιξη',
    no_show: 'Απουσία'
  }[data.status] || data.status;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}
        >
          ←
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: 'var(--navy)' }}>Λεπτομέρειες Διαβούλευσης</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: 14 }}>
            Αρ. Συνεδρίας: {data.session_number || data.id}
          </p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          <Badge color={statusColor}>{statusLabel}</Badge>
          {canCancel && (
            <button
              onClick={() => setShowCancelModal(true)}
              style={{
                background: '#DC2626',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer'
              }}
            >
              🚫 Ακύρωση Ραντεβού
            </button>
          )}
        </div>
      </div>

      {isCancelled && data.cancellation_reason && (
        <Card style={{ marginBottom: 24, borderLeft: '4px solid #DC2626' }}>
          <div style={{ color: '#DC2626', fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Αιτία Ακύρωσης</div>
          <div style={{ color: 'var(--slate)', fontSize: 13 }}>{data.cancellation_reason}</div>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div style={{ background: '#FCEEF3', border: '1px solid #E8C4D5', borderRadius: 8, padding: '14px 18px' }}>
            {/* HEADER ROW */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid #E8C4D5' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0F1F3D', letterSpacing: 0.5, textTransform: 'uppercase' }}>Στοιχεία Συνεδρίας</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0F1F3D', textAlign: 'right' }}>
                Αρ. Συνεδρίας: <span style={{ color: '#C0185A' }}>#{data.session_number || data.id}</span>
              </div>
            </div>
            {/* THREE-COLUMN GRID */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 24, fontSize: 13, color: '#0F1F3D', lineHeight: 1.7 }}>
              {/* COLUMN 1: PATIENT IDENTITY */}
              <div>
                <div style={{ display: 'flex' }}><span style={{ color: '#6B7280', minWidth: 95 }}>Ονοματεπώνυμο</span><span style={{ color: '#6B7280' }}>:&nbsp;</span><strong>{data.patient_name || '—'}</strong></div>
                <div style={{ display: 'flex' }}><span style={{ color: '#6B7280', minWidth: 95 }}>ΑΜΚΑ</span><span style={{ color: '#6B7280' }}>:&nbsp;</span><strong>{data.patient_amka || '—'}</strong></div>
                <div style={{ display: 'flex' }}><span style={{ color: '#6B7280', minWidth: 95 }}>Τηλέφωνο</span><span style={{ color: '#6B7280' }}>:&nbsp;</span><strong>{data.patient_phone || '—'}</strong></div>
                <div style={{ display: 'flex' }}><span style={{ color: '#6B7280', minWidth: 95 }}>Email</span><span style={{ color: '#6B7280' }}>:&nbsp;</span><strong style={{ wordBreak: 'break-all' }}>{data.patient_email || '—'}</strong></div>
              </div>
              {/* COLUMN 2: APPOINTMENT */}
              <div>
                <div style={{ display: 'flex' }}><span style={{ color: '#6B7280', minWidth: 95 }}>Ημερομηνία</span><span style={{ color: '#6B7280' }}>:&nbsp;</span><strong>{data.date ? new Date(data.date).toLocaleDateString('el-GR') : '—'}</strong></div>
                <div style={{ display: 'flex' }}><span style={{ color: '#6B7280', minWidth: 95 }}>Ώρα</span><span style={{ color: '#6B7280' }}>:&nbsp;</span><strong>{data.start_time?.slice(0, 5) || '—'} – {data.end_time?.slice(0, 5) || '—'}</strong></div>
                <div style={{ display: 'flex' }}><span style={{ color: '#6B7280', minWidth: 95 }}>Τύπος</span><span style={{ color: '#6B7280' }}>:&nbsp;</span><strong>{data.service_name || 'Digital GP'}</strong></div>
                <div style={{ display: 'flex' }}><span style={{ color: '#6B7280', minWidth: 95 }}>Ιατρός</span><span style={{ color: '#6B7280' }}>:&nbsp;</span><strong>Δρ. {data.doctor_name || '—'}</strong></div>
              </div>
              {/* COLUMN 3: DEMOGRAPHICS + REASON */}
              <div>
                <div style={{ display: 'flex' }}><span style={{ color: '#6B7280', minWidth: 95 }}>Γέννηση</span><span style={{ color: '#6B7280' }}>:&nbsp;</span><strong>{data.patient_dob ? new Date(data.patient_dob).toLocaleDateString('el-GR') : '—'}</strong></div>
                <div style={{ display: 'flex' }}><span style={{ color: '#6B7280', minWidth: 95 }}>Φύλο</span><span style={{ color: '#6B7280' }}>:&nbsp;</span><strong>{data.patient_gender === 'male' ? 'Άνδρας' : data.patient_gender === 'female' ? 'Γυναίκα' : '—'}</strong></div>
                {data.symptom_category && (
                  <div style={{ display: 'flex' }}><span style={{ color: '#6B7280', minWidth: 95 }}>Σύμπτωμα</span><span style={{ color: '#6B7280' }}>:&nbsp;</span><strong>{data.symptom_category}</strong></div>
                )}
              </div>
            </div>
            {/* FULL-WIDTH PATIENT NOTES */}
            {data.patient_notes && (
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #E8C4D5', fontSize: 13, color: '#0F1F3D', lineHeight: 1.6 }}>
                <span style={{ color: '#6B7280' }}>Περιγραφή από ασθενή :&nbsp;</span>
                <span>{data.patient_notes}</span>
              </div>
            )}
          </div>

          {asyncCase ? (
            <AsyncCaseView caseData={asyncCase} />
          ) : (<>
          <Card title="ΠΑΡΑΠΟΝΟ ΑΣΘΕΝΗ">
            <div style={{
              background: 'var(--bg)',
              borderRadius: 8,
              padding: 16,
              minHeight: 80,
              color: data.chief_complaint ? 'var(--navy)' : 'var(--muted)'
            }}>
              {data.chief_complaint || 'Δεν καταγράφηκε παράπονο'}
            </div>
          </Card>

          <Card title="ΔΙΑΓΝΩΣΗ">
            <div style={{
              background: 'var(--bg)',
              borderRadius: 8,
              padding: 16,
              minHeight: 80,
              color: data.diagnosis ? 'var(--navy)' : 'var(--muted)'
            }}>
              {data.diagnosis || 'Δεν καταγράφηκε διάγνωση'}
            </div>
          </Card>

          <Card title="ΚΛΙΝΙΚΕΣ ΣΗΜΕΙΩΣΕΙΣ">
            <div style={{
              background: 'var(--bg)',
              borderRadius: 8,
              padding: 16,
              minHeight: 80,
              color: data.clinical_notes ? 'var(--navy)' : 'var(--muted)'
            }}>
              {data.clinical_notes || 'Δεν υπάρχουν σημειώσεις'}
            </div>
          </Card>

          <Card title="ΣΥΝΤΑΓΟΓΡΑΦΗΣΗ">
            <div style={{
              background: 'var(--bg)',
              borderRadius: 8,
              padding: 16,
              minHeight: 80,
              color: data.prescription ? 'var(--navy)' : 'var(--muted)'
            }}>
              {data.prescription || 'Δεν υπάρχει συνταγογράφηση'}
            </div>
          </Card>

          {data.follow_up_notes && (
            <Card title="ΠΑΡΑΚΟΛΟΥΘΗΣΗ">
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: 16 }}>
                {data.follow_up_notes}
              </div>
            </Card>
          )}
          </>)}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Card title="ΣΤΟΙΧΕΙΑ ΑΣΘΕΝΗ">
            <InfoRow label="Ονοματεπώνυμο" value={data.patient_name} />
            <InfoRow label="AMKA" value={data.patient_amka} />
            <InfoRow label="Ημ. Γέννησης" value={data.patient_dob ? new Date(data.patient_dob).toLocaleDateString('el-GR') : null} />
            <InfoRow label="Φύλο" value={data.patient_gender === 'male' ? 'Άνδρας' : data.patient_gender === 'female' ? 'Γυναίκα' : null} />
            <InfoRow label="Email" value={data.patient_email} />
            <InfoRow label="Τηλέφωνο" value={data.patient_phone} />
          </Card>

          <Card title="ΡΑΝΤΕΒΟΥ">
            <InfoRow label="Ημερομηνία" value={data.date ? new Date(data.date).toLocaleDateString('el-GR') : null} />
            <InfoRow label="Ώρα" value={`${data.start_time?.slice(0, 5) || ''} – ${data.end_time?.slice(0, 5) || ''}`} />
            <InfoRow label="Υπηρεσία" value={data.service_name} />
            <InfoRow label="Κατάσταση" value={statusLabel} highlight />
            <InfoRow label="Αρ. Συνεδρίας" value={data.session_number} />
            {/* @admin-outcome-rows-v1 (30 Sep 2026) -- M-52. The detail endpoint
                has returned consultation_outcome and follow_up_required all
                along and neither was rendered, so an admin could read
                "Ολοκληρώθηκε" without seeing that the patient never turned up. */}
            {data.consultation_outcome && (
              <InfoRow
                label="Έκβαση"
                value={{
                  completed: 'Ολοκληρώθηκε',
                  dna: 'Ο ασθενής δεν προσήλθε',
                  abandoned: 'Εγκαταλείφθηκε',
                  doctor_no_show: 'Ο ιατρός δεν προσήλθε',
                }[data.consultation_outcome] || data.consultation_outcome}
              />
            )}
            {(data.follow_up_required === true || data.follow_up_required === false) && (
              <InfoRow label="Επανεξέταση" value={data.follow_up_required ? 'Ναι' : 'Όχι'} />
            )}
          </Card>

          <Card title="ΙΑΤΡΟΣ">
            <InfoRow label="Όνομα" value={`Δρ. ${data.doctor_name}`} />
            <InfoRow label="Ειδικότητα" value={data.doctor_specialty} />
          </Card>

          {data.rating && (
            <Card title="ΑΞΙΟΛΟΓΗΣΗ">
              <div style={{ textAlign: 'center', padding: 16 }}>
                <div style={{ fontSize: 36, color: 'var(--warning)' }}>
                  {'★'.repeat(data.rating)}{'☆'.repeat(5 - data.rating)}
                </div>
                <div style={{ fontSize: 14, color: 'var(--muted)', marginTop: 8 }}>
                  {data.rating}/5
                </div>
                {data.feedback_comment && (
                  <div style={{ marginTop: 16, padding: 12, background: 'var(--bg)', borderRadius: 8, fontSize: 13, color: 'var(--navy)' }}>
                    "{data.feedback_comment}"
                  </div>
                )}
              </div>
            </Card>
          )}

          {data.payment_amount && (
            <Card title="ΠΛΗΡΩΜΗ">
              <InfoRow label="Ποσό" value={`€${Number(data.payment_amount).toFixed(2)}`} highlight />
              <InfoRow label="Κατάσταση" value={data.payment_status === 'completed' ? 'Πληρώθηκε' : 'Εκκρεμεί'} />
              {/* @billing-paymentmethod-fix-detail  Mirror of AllPages.jsx fix — render localized label, not raw enum string. */}
              <InfoRow label="Μέθοδος" value={
                data.payment_method === 'card'       ? '💳 Κάρτα'      :
                data.payment_method === 'apple_pay'  ? '🍎 Apple Pay'  :
                data.payment_method === 'google_pay' ? '🤖 Google Pay' :
                data.payment_method
              } />
            </Card>
          )}
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white', borderRadius: 12, padding: 24,
            width: '90%', maxWidth: 500, boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
          }}>
            <h2 style={{ margin: '0 0 16px', color: 'var(--navy)', fontSize: 20 }}>
              🚫 Ακύρωση Ραντεβού
            </h2>
            <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 16 }}>
              Το ραντεβού θα επισημανθεί ως ακυρωμένο. Το slot θα παραμείνει δεσμευμένο.
            </p>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
              Αιτία Ακύρωσης (υποχρεωτικό)
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={4}
              placeholder="π.χ. Ο ασθενής ακύρωσε τηλεφωνικά..."
              style={{
                width: '100%', padding: 12, borderRadius: 8,
                border: '1px solid var(--border)', resize: 'vertical',
                fontFamily: 'inherit', fontSize: 14
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                onClick={() => { setShowCancelModal(false); setCancelReason(''); }}
                disabled={cancelling}
                style={{
                  padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border)',
                  background: 'white', cursor: 'pointer', fontWeight: 600
                }}
              >
                Ακύρωση
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling || !cancelReason.trim()}
                style={{
                  padding: '10px 20px', borderRadius: 8, border: 'none',
                  background: '#DC2626', color: 'white', cursor: 'pointer',
                  fontWeight: 600, opacity: cancelling ? 0.6 : 1
                }}
              >
                {cancelling ? 'Ακύρωση...' : '🚫 Επιβεβαίωση Ακύρωσης'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
